<script>
import { debounce } from 'lodash'
import baseMixin from '@/mixins/baseMixin'
import dateMixin from '@/mixins/dateMixin'
import VuetifyTextClass from '@/shared/constants/core/VuetifyTextClass'
import VuetifyColorClass from '@/shared/constants/core/VuetifyColourClass'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import { DurationUnits } from '@/shared/constants/date/DurationUnits'

const MAX_END_DATE_DAYS = 90
const MAX_START_DATE_DAYS = 60
const DATE_UNIT = DurationUnits.DAY

export default {
  name: 'ExtendBookingDatePicker',
  mixins: [baseMixin, dateMixin],
  props: {
    booking: {
      type: Object,
      required: true,
    },
    extensionStartDate: {
      type: String,
      default: '',
    },
    extensionEndDate: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      ...VuetifyTextClass,
      ...VuetifyColorClass,
      showEndDatePicker: false,
      showStartDatePicker: false,
      isValid: true,
      internalStartDate: this.extensionStartDate || '',
      internalEndDate: this.extensionEndDate || '',
    }
  },
  computed: {
    bookingDescription() {
      const days = this.booking?.blockDetails?.bookingsCount || 1
      const prefix = this.$t('booking.extend.bodyTextPrefix')
      const suffix = this.$t('booking.extend.bodyTextSuffix')

      return `${prefix} ${days} ${this.$tc(
        'booking.extend.days',
        days
      )} ${suffix} ${this.friendlyEndDate}.`
    },
    endDate() {
      return (
        this.booking?.blockDetails?.endDateLocal ??
        this.booking.endDateTimeLocal
      )
    },
    friendlyEndDate() {
      return this.endDate ? this.formatDateToFriendlyString(this.endDate) : ''
    },
    minDate() {
      const today = this.getTodaysDate()
      const date = this.isBefore(this.endDate, today) ? today : this.endDate
      return this.formatDate(
        this.dateAdd(date, 1, DurationUnits.DAY),
        DateFormatToken.YYYYMMDD
      )
    },
    maxAllowedStartDate() {
      return this.formatDate(
        this.dateAdd(this.endDate, MAX_START_DATE_DAYS, DATE_UNIT),
        DateFormatToken.YYYYMMDD
      )
    },
    maxAllowedEndDate() {
      return this.formatDate(
        this.dateAdd(this.minDate, MAX_END_DATE_DAYS, DATE_UNIT),
        DateFormatToken.YYYYMMDD
      )
    },
  },
  watch: {
    internalStartDate(newStart) {
      if (
        this.internalEndDate &&
        this.isBefore(this.internalEndDate, newStart)
      ) {
        this.internalEndDate = ''
      }
      this.debouncedValidateAndEmit()
    },
    internalEndDate() {
      this.debouncedValidateAndEmit()
    },
  },
  methods: {
    debouncedValidateAndEmit: debounce(function() {
      this.validateAndEmit()
    }, 150),
    openStartDatePicker() {
      if (
        !this.internalStartDate ||
        !this.isValidDate(this.internalStartDate)
      ) {
        this.internalStartDate = this.minDate
      }
      this.showStartDatePicker = !this.showStartDatePicker
    },
    openEndDatePicker() {
      if (!this.internalEndDate || !this.isValid) {
        this.internalEndDate = this.internalStartDate || this.minDate
      }
      this.showEndDatePicker = !this.showEndDatePicker
    },
    validateAndEmit() {
      if (this.$refs.form) this.$refs.form.validate()
      this.$emit('update', {
        start: this.internalStartDate || '',
        end: this.internalEndDate || '',
        isValid: this.isValid,
      })
    },
    validDateRule(value) {
      if (!value) return true
      return this.isValidDate(value) || this.$t('input.validDate')
    },
    futureDatesOnlyRule(selectedDate) {
      if (!selectedDate) return true
      const today = this.startOf(this.getTodaysDate(), DurationUnits.DAY)
      return (
        this.isSameOrAfter(selectedDate, today) ||
        this.$t('input.dateInPastValidationError')
      )
    },
    maxStartDateRule(selectedDate) {
      if (!selectedDate) return true

      if (this.isAfter(selectedDate, this.maxAllowedStartDate)) {
        return this.$t('booking.extend.startDateMaxDaysValidationError')
      }
      return true
    },
    minDateRule(selectedDate) {
      if (!selectedDate) return true
      return (
        this.isSameOrAfter(selectedDate, this.minDate) ||
        this.$t('booking.extend.startDateMinDateValidationError')
      )
    },
    minEndDateRule(selectedDate) {
      if (!selectedDate) return true
      const min = this.internalStartDate || this.minDate

      return (
        this.isSameOrAfter(selectedDate, min) ||
        this.$t('booking.extend.endDateOnOrAfterStartValidationError')
      )
    },
    maxEndDateRule(selectedDate) {
      if (!selectedDate) return true
      return (
        this.isSameOrBefore(selectedDate, this.maxAllowedEndDate) ||
        this.$t('booking.extend.maxExtensionValidationError')
      )
    },
  },
}
</script>

<template>
  <div>
    <p :class="smallBody">{{ bookingDescription }}</p>
    <v-form ref="form" v-model="isValid" class="mt-6">
      <p :class="[smallBody, 'font-weight-medium ']">
        {{ $t('booking.extend.inputLabel') }}
      </p>
      <div
        class="date-fields-wrapper d-flex align-start"
        :class="{ 'flex-wrap': !isMediumOrHigher }"
      >
        <v-menu
          ref="menuStart"
          v-model="showStartDatePicker"
          class="mr-4"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          rounded="lg"
        >
          <template v-slot:activator="{ attrs }">
            <v-text-field
              v-model="internalStartDate"
              :label="$t('booking.extend.startDate')"
              type="date"
              append-icon="mdi-calendar"
              outlined
              dense
              class="date-field mr-6"
              :class="smallBody"
              :rules="[
                validDateRule,
                futureDatesOnlyRule,
                minDateRule,
                maxStartDateRule,
              ]"
              v-bind="attrs"
              @click:append="openStartDatePicker"
            />
          </template>
          <v-date-picker
            v-model="internalStartDate"
            :color="colorClass.blue"
            no-title
            :first-day-of-week="firstDayOfWeek"
            :min="minDate"
            :max="maxAllowedStartDate"
            @input="showStartDatePicker = false"
          ></v-date-picker>
        </v-menu>

        <v-menu
          ref="menu"
          v-model="showEndDatePicker"
          class="flex-grow-1"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          rounded="lg"
        >
          <template v-slot:activator="{ attrs }">
            <v-text-field
              v-model="internalEndDate"
              :label="$t('booking.extend.endDate')"
              type="date"
              append-icon="mdi-calendar"
              outlined
              dense
              class="date-field"
              :class="smallBody"
              :rules="[
                validDateRule,
                futureDatesOnlyRule,
                minEndDateRule,
                maxEndDateRule,
              ]"
              v-bind="attrs"
              @click:append="openEndDatePicker"
            />
          </template>
          <v-date-picker
            v-model="internalEndDate"
            :color="colorClass.blue"
            no-title
            :first-day-of-week="firstDayOfWeek"
            :min="internalStartDate || minDate"
            :max="maxAllowedEndDate"
            @input="showEndDatePicker = false"
          ></v-date-picker>
        </v-menu>
      </div>
    </v-form>
  </div>
</template>

<style lang="scss" scoped>
.date-fields-wrapper {
  max-width: 400px;
}

.date-field {
  border-radius: 8px;

  ::v-deep input[type='date']::-webkit-inner-spin-button,
  ::v-deep input[type='date']::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }
}
</style>
